import FormControl from '@mui/material/FormControl';
import cn from 'clsx';
import React, { useState, useImperativeHandle, forwardRef, useEffect } from 'react';
import { Errors, Step2Props } from './interfaces';
import { useBoolean, useTranslation } from 'hooks';
import 'views/components/central-monitoring/panels/add-panel/styles.scss';
import { PanelDisplayBy, PanelOrderBy } from 'types/graphql-global-types';
import { checkRequiredFields, validateFields } from 'utils/validation';
import DataSourceSelector from 'views/components/central-monitoring/data-source-selector';
import { FieldSelector, Formats } from 'views/components/central-monitoring/field-selector';
import PanelConstants from 'views/components/central-monitoring/panels/add-panel/panel-constants';
import SelectWithCategories from 'views/components/core/select-with-categories';
import Constants from '@utils/constants';

const { columnOrders, displayByForStacked } = PanelConstants;

interface Step2StackedColumnState {
    dataSource: any;
    xAxisDataField: string;
    sortField: string;
    orderBy: PanelOrderBy;
    displayBy: PanelDisplayBy;
    scheduleId: string;
}

interface Step2StackedColumnErrors extends Errors {
    dataSource?: string;
    xAxisDataField?: string;
    sortField?: string;
    orderBy?: string;
    displayBy?: string;
    scheduleId?: string;
}

const Step2StackedColumn = forwardRef(
    (
        {
            panelState,
            setPanelState,
            setPage,
            page,
            updateState,
            show,
            updateErrorState,
            canSave,
            scheduleOptions,
        }: Step2Props,
        ref,
    ) => {
        const { t } = useTranslation();

        const initialState: Step2StackedColumnState = {
            dataSource: panelState.dataSource.dataSource,
            xAxisDataField: panelState.dataSource.xAxisDataField,
            sortField: panelState.dataSource.sortField,
            orderBy: panelState.graph.orderBy || PanelOrderBy.LABEL_ASCENDING,
            displayBy: panelState.graph.displayBy || PanelDisplayBy.SITE,
            scheduleId: panelState.scheduleId,
        };

        const errorsState: Step2StackedColumnErrors = {
            dataSource: '',
            xAxisDataField: '',
            sortField: '',
            orderBy: '',
            displayBy: '',
            scheduleId: '',
        };

        const [state, setState] = useState({ ...initialState } as Step2StackedColumnState);
        const [errors, setErrors] = useState({ ...errorsState } as Step2StackedColumnErrors);
        const scheduleInactive = useBoolean(false);

        useEffect(() => {
            const scheduleOption = scheduleOptions.find((x: any) => x.id === state.scheduleId);
            scheduleInactive.setValue(scheduleOption?.id === state.scheduleId && scheduleOption?.active === false);
        }, [state.scheduleId, scheduleOptions]);

        useEffect(() => {
            if (panelState)
                setState({
                    dataSource: panelState.dataSource.dataSource,
                    xAxisDataField: panelState.dataSource.xAxisDataField,
                    sortField: panelState.dataSource.sortField,
                    orderBy: panelState.graph.orderBy || PanelOrderBy.LABEL_ASCENDING,
                    displayBy: panelState.graph.displayBy || PanelDisplayBy.SITE,
                    scheduleId: panelState.scheduleId,
                });
        }, [panelState]);

        function onChangeRowData(value: string) {
            setState({
                ...state,
                dataSource: value,
                xAxisDataField: '',
                sortField: '',
            });
            updateState({ target: { name: 'dataSource.dataSource', value } });
            updateState({ target: { name: 'dataSource.xAxisDataField', value: '' } });
            updateState({ target: { name: 'dataSource.sortField', value: '' } });
        }

        const validateXAxisAndStackedBy = (value?: string) => {
            if (value !== PanelDisplayBy.EVENT) {
                return true;
            }
            // If both are blank, we'll return true so validation can mark as required instead
            if (!state.xAxisDataField && !state.sortField) {
                return true;
            }

            return state.xAxisDataField !== state.sortField;
        };

        const validationOnUpdate = () => {
            const updatedErrors: Step2StackedColumnErrors = { ...errors };
            const requiredFields = ['dataSource', 'displayBy'];
            const isDefaultScheduleId =
                state.orderBy === PanelOrderBy.LABEL_VISIT_SEQUENCE_NUMBER &&
                state.scheduleId === Constants.ObjectId.Empty;

            updatedErrors.xAxisDataField = '';
            updatedErrors.sortField = '';

            const validXAxisAndStackedBy = validateXAxisAndStackedBy(panelState.graph.displayBy);

            if (!validXAxisAndStackedBy) {
                updatedErrors.xAxisDataField = t('admin:cannotBeSameAsStackedBy');
                updatedErrors.sortField = t('admin:cannotBeSameAsXAxis');
            } else {
                if (panelState.graph.displayBy === PanelDisplayBy.EVENT) requiredFields.push('xAxisDataField');
                requiredFields.push('sortField');
            }

            if (state.orderBy === PanelOrderBy.LABEL_VISIT_SEQUENCE_NUMBER) requiredFields.push('scheduleId');

            const updateErrors = (errs: { [key: string]: string }[]) => {
                setErrors({
                    ...errs,
                    ...(isDefaultScheduleId ? { scheduleId: t('ra:requiredFieldError') } : {}),
                });
            };

            return (
                checkRequiredFields({
                    fields: requiredFields,
                    state,
                    setErrors: updateErrors,
                    errors: updatedErrors,
                }) &&
                validXAxisAndStackedBy &&
                !isDefaultScheduleId
            );
        };

        const validate = (value: string, fieldName: string, length?: number, required?: boolean) => {
            let updatedErrors: Step2StackedColumnErrors = validateFields(value, fieldName, length, required);
            if (fieldName === 'displayBy' && validateXAxisAndStackedBy(value)) {
                updatedErrors = { ...updatedErrors, xAxisDataField: '', sortField: '' };
            }
            if (fieldName === 'dataSource') {
                updatedErrors = { ...updatedErrors, xAxisDataField: '', sortField: '' };
            } else if (fieldName === 'sortField') {
                updatedErrors = { ...updatedErrors, xAxisDataField: '' };
            } else if (fieldName === 'orderBy') {
                updatedErrors = { ...updatedErrors, scheduleId: '' };
            } else if (fieldName === 'xAxisDataField') updatedErrors = { ...updatedErrors, sortField: '' };

            if (
                fieldName === 'scheduleId' &&
                state.orderBy === PanelOrderBy.LABEL_VISIT_SEQUENCE_NUMBER &&
                value === Constants.ObjectId.Empty
            ) {
                updatedErrors = { ...updatedErrors, scheduleId: t('ra:requiredFieldError') };
            }

            setErrors({ ...errors, ...updatedErrors });
        };

        const onChange = (fieldName: string, length?: number, required?: boolean, key?: string, value: string = '') => {
            if (fieldName === 'dataSource') {
                onChangeRowData(value);
            } else if (fieldName === 'sortField') {
                updateState({ target: { name: 'dataSource.sortField', value } });
            } else {
                setState({ ...state, [fieldName]: value });
                updateState({ target: { name: key || fieldName, value } });
                if (fieldName === 'orderBy') {
                    updateState({ target: { name: 'scheduleId', value: '' } });
                }
                if (fieldName === 'xAxisDataField') {
                    updateState({
                        target: {
                            name: 'graph.orderBy',
                            value:
                                value !== 'SubjectVisit.visitName' && state.orderBy === 'LABEL_VISIT_SEQUENCE_NUMBER'
                                    ? PanelOrderBy.VOLUME_DESCENDING
                                    : state.orderBy,
                        },
                    });
                }
            }

            validate(value, fieldName, length, required);
        };

        const onNext = () => {
            if (validationOnUpdate()) {
                setPanelState({
                    ...panelState,
                    graph: {
                        ...panelState.graph,
                        displayBy: state.displayBy,
                    },
                    dataSource: {
                        ...panelState.dataSource,
                        dataSource: state.dataSource,
                        xAxisDataField: state.xAxisDataField,
                        sortField: state.sortField,
                    },
                    modelParams: {
                        ...panelState.modelParams,
                    },
                });
                setPage({ ...page, number: 3, available: 3 });
            } else if (page.number !== 2) setPage({ ...page, number: 2, available: page.available });
        };

        const cleanStep = () => {
            setState({ ...initialState });
            setErrors({ ...errorsState });
        };

        useEffect(() => {
            updateErrorState(errors, 2);
        }, [errors]);

        useImperativeHandle(ref, () => ({
            nextButton() {
                onNext();
            },
            cleanStep() {
                cleanStep();
            },
            validationOnUpdate() {
                return validationOnUpdate();
            },
        }));

        const getScheduleNameRFormControl = (mr: boolean) => (
            <FormControl variant="outlined" className={cn('form-control-type', 'not-full', { mr })}>
                <SelectWithCategories
                    label={t('admin:scheduleNameR')}
                    dataArray={scheduleOptions}
                    onChange={(_?: string) => (event: any) => {
                        onChange('scheduleId', undefined, true, 'scheduleId', event.target?.value);
                    }}
                    fieldSelect="scheduleId"
                    currentState={state.scheduleId}
                    labelField="schedule"
                    showWarning={scheduleInactive.value}
                    customLabelId="schedule-id"
                    customId="schedule-id"
                    error={errors.scheduleId}
                    customClassName={`${scheduleInactive.value ? 'form-action-type inactive' : 'form-action-type'}`}
                />
            </FormControl>
        );

        return (
            <div style={!show ? { display: 'none' } : {}}>
                <div className="type-edc-wrapper mt mb-30">
                    <FormControl variant="outlined" className="form-control-type not-full mr">
                        <SelectWithCategories
                            label={t('admin:displayBy')}
                            dataArray={displayByForStacked}
                            fieldSelect="displayBy"
                            onChange={(fieldName?: string) => (event: any) => {
                                if (fieldName)
                                    onChange(fieldName, undefined, false, 'graph.displayBy', event.target?.value);
                            }}
                            currentState={state.displayBy}
                            labelField="displayBy"
                            showWarning={false}
                            filterable={false}
                            customLabelId="display-by-label-id"
                            customId="display-by-id"
                            customClassName="edit-atts-column-category"
                            error={errors.displayBy}
                            disabled={!canSave}
                        />
                    </FormControl>
                    <FormControl variant="outlined" className="form-control-type not-full">
                        <DataSourceSelector
                            label={t('admin:rowData')}
                            onChange={(value: string) => {
                                onChange('dataSource', undefined, false, 'dataSource.dataSource', value);
                            }}
                            fieldSelect="dataSource"
                            value={state.dataSource}
                            labelField="dataSource"
                            showDefault={false}
                            error={errors.dataSource}
                            customClassName="edit-atts-category"
                            disabled={!canSave}
                        />
                    </FormControl>
                </div>
                <div className="type-edc-wrapper mt mb-30">
                    <FormControl variant="outlined" className="form-control-type not-full mr">
                        <SelectWithCategories
                            label={t('admin:ColumnOrder')}
                            dataArray={columnOrders}
                            onChange={(fieldName?: string) => (event: any) => {
                                if (fieldName)
                                    onChange(fieldName, undefined, false, 'graph.orderBy', event.target?.value);
                            }}
                            fieldSelect="orderBy"
                            currentState={state.orderBy}
                            labelField="orderBy"
                            showWarning={false}
                            filterable={false}
                            customLabelId="orderby-column-label-id"
                            customId="orderby-id"
                            customClassName="edit-atts-column-category"
                            error={errors.orderBy}
                            disabled={!canSave}
                        />
                    </FormControl>
                    {state.displayBy === PanelDisplayBy.EVENT ? (
                        <FormControl variant="outlined" className="form-control-type not-full">
                            <FieldSelector
                                label={t('admin:xAxisCategory')}
                                dataSource={state.dataSource}
                                formats={[Formats.Text, Formats.Int, Formats.Bool]}
                                value={state.xAxisDataField}
                                onChange={(value: string) => {
                                    onChange('xAxisDataField', undefined, false, 'dataSource.xAxisDataField', value);
                                }}
                                labelField="xAxisDataField"
                                fieldSelect="xAxisDataField"
                                customId="x-axis-category-id"
                                error={errors.xAxisDataField}
                                searchable
                                disabled={!canSave}
                            />
                        </FormControl>
                    ) : null}
                    {state.orderBy === PanelOrderBy.LABEL_VISIT_SEQUENCE_NUMBER &&
                        state.displayBy !== PanelDisplayBy.EVENT &&
                        getScheduleNameRFormControl(false)}
                </div>
                <div className="type-edc-wrapper mt mb-30">
                    {state.orderBy === PanelOrderBy.LABEL_VISIT_SEQUENCE_NUMBER &&
                        state.displayBy === PanelDisplayBy.EVENT &&
                        getScheduleNameRFormControl(true)}
                    <FormControl variant="outlined" className="form-control-type not-full">
                        <FieldSelector
                            label={t('admin:stackedBy')}
                            dataSource={state.dataSource}
                            formats={[Formats.Text, Formats.Int, Formats.Bool]}
                            value={state.sortField}
                            labelField="sortField"
                            fieldSelect="sortField"
                            onChange={(value: string) => {
                                onChange('sortField', undefined, false, 'dataSource.sortField', value);
                            }}
                            customId="sortField-column-id"
                            error={errors.sortField}
                            searchable
                            disabled={!canSave}
                        />
                    </FormControl>
                </div>
            </div>
        );
    },
);
export default Step2StackedColumn;
